import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, errMsg, formatPrice, resolveImage } from "../../lib/api";
import { Plus, Pencil, Trash2, X, UploadCloud, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";
import HolographicPlaceholder from "../../components/HolographicPlaceholder";

const empty = {
  slug: "", name: "", category: "booster-boxes", price_cents: 0,
  description: "", rarity: "", stock: 0, tag: "", old_price_cents: null, image_url: "",
};

function slugify(s = "") {
  return s.toLowerCase().trim()
    .replace(/['"]/g, "")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

export default function AdminProducts() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [editing, setEditing] = useState(null);
  const [search, setSearch] = useState("");

  const reload = async () => {
    const { data } = await api.get("/admin/products");
    setProducts(data);
  };

  useEffect(() => {
    reload().catch(() => {});
    api.get("/categories").then(({ data }) => setCategories(data)).catch(() => {});
  }, []);

  const filtered = useMemo(() => {
    const s = search.toLowerCase();
    return products.filter((p) =>
      !s || p.name.toLowerCase().includes(s) || p.slug.toLowerCase().includes(s) || p.category.toLowerCase().includes(s)
    );
  }, [products, search]);

  const onDelete = async (p) => {
    if (!window.confirm(`Delete "${p.name}"?`)) return;
    try {
      await api.delete(`/admin/products/${p.id}`);
      toast.success("Product deleted");
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const onClearAll = async () => {
    const phrase = window.prompt(
      `This will permanently delete ALL ${products.length} products. This can't be undone.\n\nType DELETE ALL (in capitals) to confirm:`
    );
    if (phrase !== "DELETE ALL") {
      if (phrase !== null) toast.error("Confirmation phrase didn't match — nothing deleted");
      return;
    }
    try {
      const { data } = await api.post("/admin/products/clear-all", { confirm: "DELETE ALL" });
      toast.success(`Deleted ${data.deleted} products`);
      reload();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="font-display font-extrabold text-4xl tracking-tighter text-white">Products</h1>
          <p className="mt-1 text-sm text-white/50">{products.length} total</p>
        </div>
        <div className="flex items-center gap-3">
          <input
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search products…"
            data-testid="admin-products-search"
            className="bg-[#110E1D] border border-white/10 rounded-md px-3 py-2 text-sm w-56 outline-none focus:border-[#B026FF]/60"
          />
          {products.length > 0 && (
            <button
              onClick={onClearAll}
              data-testid="admin-clear-all-btn"
              className="h-10 px-4 rounded-md inline-flex items-center gap-2 text-sm text-red-300 bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 hover:border-red-500/50 transition"
            >
              <Trash2 size={14} /> Delete all
            </button>
          )}
          <button
            onClick={() => setEditing({ ...empty })}
            data-testid="admin-add-product-btn"
            className="btn-violet h-10 px-4 rounded-md inline-flex items-center gap-2 text-sm"
          >
            <Plus size={14} /> Add Product
          </button>
        </div>
      </div>

      <div className="mt-8 card-elev rounded-xl overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#0A0812] text-[10px] tracking-[0.22em] uppercase text-white/40 font-bold">
            <tr>
              <th className="text-left px-5 py-3">Image</th>
              <th className="text-left px-5 py-3">Name</th>
              <th className="text-left px-5 py-3">Category</th>
              <th className="text-right px-5 py-3">Price</th>
              <th className="text-right px-5 py-3">Stock</th>
              <th className="text-left px-5 py-3">Tag</th>
              <th className="text-right px-5 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((p) => (
              <tr key={p.id} className="border-t border-white/5 hover:bg-white/[0.02]" data-testid={`product-row-${p.slug}`}>
                <td className="px-5 py-3">
                  <div className="w-12 h-16 rounded-md overflow-hidden bg-[#0A0812] border border-white/10 flex items-center justify-center">
                    {p.image_url ? (
                      <img src={resolveImage(p.image_url)} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <ImageIcon size={14} className="text-white/30" />
                    )}
                  </div>
                </td>
                <td className="px-5 py-3">
                  <div className="text-white">{p.name}</div>
                  <div className="text-xs text-white/40 font-mono">{p.slug}</div>
                </td>
                <td className="px-5 py-3 text-white/70">{p.category.replace(/-/g, ' ')}</td>
                <td className="px-5 py-3 text-right font-mono text-white">{formatPrice(p.price_cents)}</td>
                <td className="px-5 py-3 text-right font-mono text-white/70">{p.stock}</td>
                <td className="px-5 py-3 text-[#B026FF] text-xs uppercase tracking-wider">{p.tag || ""}</td>
                <td className="px-5 py-3 text-right">
                  <button onClick={() => setEditing(p)} data-testid={`edit-product-${p.slug}`} className="text-white/60 hover:text-white p-1">
                    <Pencil size={14} />
                  </button>
                  <button onClick={() => onDelete(p)} data-testid={`delete-product-${p.slug}`} className="text-white/40 hover:text-red-400 p-1 ml-1">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-10 text-center text-white/50">No products</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {editing && (
        <ProductDialog
          product={editing}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}
    </div>
  );
}

function ProductDialog({ product, categories, onClose, onSaved }) {
  const isNew = !product.id;
  const fileRef = useRef(null);

  // Convert cents <-> pounds for display
  const toPounds = (c) => (c == null || c === "" ? "" : (Number(c) / 100).toFixed(2));
  const toPence = (p) => (p === "" || p == null ? null : Math.round(Number(p) * 100));

  const [form, setForm] = useState({
    slug: product.slug || "",
    name: product.name || "",
    category: product.category || "booster-boxes",
    price_pounds: toPounds(product.price_cents) || "",
    description: product.description || "",
    rarity: product.rarity || "",
    stock: product.stock ?? 0,
    tag: product.tag || "",
    old_price_pounds: toPounds(product.old_price_cents) || "",
    image_url: product.image_url || "",
  });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState("");

  const set = (k) => (e) => {
    const val = e.target.type === "number" ? e.target.value : e.target.value;
    setForm((f) => {
      const next = { ...f, [k]: val };
      // Auto-generate slug from name (only when creating and slug is empty/derived)
      if (k === "name" && isNew && (!f.slug || f.slug === slugify(f.name))) {
        next.slug = slugify(val);
      }
      return next;
    });
  };

  const onUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    setError("");
    try {
      const data = new FormData();
      data.append("file", file);
      const res = await api.post("/admin/upload", data, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: res.data.url }));
      toast.success("Image uploaded");
    } catch (e) {
      setError(errMsg(e, "Upload failed"));
    } finally {
      setUploading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const body = {
        slug: form.slug,
        name: form.name,
        category: form.category,
        price_cents: toPence(form.price_pounds) ?? 0,
        old_price_cents: toPence(form.old_price_pounds),
        stock: Number(form.stock) || 0,
        rarity: form.rarity || null,
        tag: form.tag || null,
        description: form.description || "",
        image_url: form.image_url || null,
      };
      if (isNew) {
        await api.post("/admin/products", body);
        toast.success("Product created");
      } else {
        await api.patch(`/admin/products/${product.id}`, body);
        toast.success("Product updated");
      }
      onSaved();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-[#0A0812] border border-white/10 rounded-xl w-full max-w-3xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="px-7 py-5 border-b border-white/5 flex items-center justify-between sticky top-0 bg-[#0A0812] z-10">
          <h2 className="font-display text-2xl text-white">{isNew ? "New Product" : "Edit Product"}</h2>
          <button onClick={onClose} className="text-white/50 hover:text-white"><X size={18} /></button>
        </div>
        <form onSubmit={submit} className="p-7">
          <div className="grid grid-cols-12 gap-6">
            {/* Image uploader column */}
            <div className="col-span-12 md:col-span-4">
              <div className="block text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Image</div>
              <div className="aspect-[3/4] rounded-lg overflow-hidden mb-3">
                <HolographicPlaceholder label={form.name || "New"} subLabel={form.category} ratio="aspect-[3/4]" imageUrl={form.image_url} />
              </div>
              <input ref={fileRef} type="file" accept="image/*" onChange={(e) => onUpload(e.target.files?.[0])} className="hidden" data-testid="product-form-file-input" />
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                disabled={uploading}
                data-testid="product-form-upload-btn"
                className="btn-ghost-violet w-full h-10 rounded-md text-sm inline-flex items-center justify-center gap-2 disabled:opacity-50"
              >
                <UploadCloud size={14} /> {uploading ? "Uploading…" : form.image_url ? "Replace image" : "Upload image"}
              </button>
              {form.image_url && (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, image_url: "" }))}
                  className="mt-2 text-xs text-white/50 hover:text-red-300 w-full"
                  data-testid="product-form-image-clear"
                >
                  Remove image
                </button>
              )}
              <div className="mt-3 text-[11px] text-white/40 leading-relaxed">
                JPG, PNG, WEBP or GIF. Max 5 MB. If you don't upload one, the holographic placeholder is used automatically.
              </div>
            </div>

            {/* Fields */}
            <div className="col-span-12 md:col-span-8 space-y-5">
              <Field label="Name">
                <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="product-form-name" />
              </Field>
              <Field label="Slug (URL)" hint={`kavocards.com/product/${form.slug || "your-slug"}`}>
                <input required value={form.slug} onChange={set("slug")} placeholder="my-product-slug" className={inputCls} data-testid="product-form-slug" />
              </Field>
              <div className="grid grid-cols-2 gap-4">
                <Field label="Category">
                  <select value={form.category} onChange={set("category")} className={inputCls} data-testid="product-form-category">
                    {categories.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </Field>
                <Field label="Tag (optional)">
                  <input value={form.tag} onChange={set("tag")} placeholder="Hot, Low Stock, Chase…" className={inputCls} data-testid="product-form-tag" />
                </Field>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <Field label="Price (£)">
                  <input type="number" required step="0.01" min={0} value={form.price_pounds} onChange={set("price_pounds")} className={inputCls} data-testid="product-form-price" placeholder="29.99" />
                </Field>
                <Field label="Compare-at (£)">
                  <input type="number" step="0.01" min={0} value={form.old_price_pounds} onChange={set("old_price_pounds")} className={inputCls} data-testid="product-form-old-price" placeholder="39.99" />
                </Field>
                <Field label="Stock">
                  <input type="number" required min={0} value={form.stock} onChange={set("stock")} className={inputCls} data-testid="product-form-stock" />
                </Field>
              </div>
              <Field label="Rarity (optional)">
                <input value={form.rarity} onChange={set("rarity")} placeholder="Sealed, Alt Art, Holo, etc." className={inputCls} data-testid="product-form-rarity" />
              </Field>
              <Field label="Description">
                <textarea rows={4} value={form.description} onChange={set("description")} className={inputCls + " resize-none"} data-testid="product-form-description" />
              </Field>
            </div>
          </div>

          {error && <div className="mt-5 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}

          <div className="flex justify-end gap-3 pt-6 mt-6 border-t border-white/5">
            <button type="button" onClick={onClose} className="btn-ghost-violet h-11 px-5 rounded-md text-sm">Cancel</button>
            <button type="submit" disabled={busy} data-testid="product-form-submit" className="btn-violet h-11 px-6 rounded-md text-sm disabled:opacity-50">
              {busy ? "Saving…" : isNew ? "Create Product" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const inputCls = "w-full bg-[#110E1D] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white outline-none focus:border-[#B026FF]/60";

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between mb-2">
        <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold">{label}</div>
        {hint && <div className="text-[10px] text-white/30 font-mono truncate ml-2">{hint}</div>}
      </div>
      {children}
    </label>
  );
}
